import { supabase } from "../../lib/supabase"

/*
  CONFERENCE REGISTRATIONS SERVICE

  Responsibilities
  - Fetch full registration list (admin table)
  - Fetch total registrations count
  - Fetch daily aggregation
  - Fetch monthly aggregation
*/

/* ===============================
   Full conference registrations
   Used by ConferenceRegistrations.jsx
================================ */
export async function fetchConferenceRegistrations() {
  const { data, error } = await supabase
    .from("conference_registrations")
    .select("*")
    .order("created_at", { ascending: false })

  if (error) {
    console.error("Fetch conference registrations error:", error)
    return { data: [] }
  }

  return { data }
}

/* ===============================
   Total conference registrations
   Used by DashboardOverview.jsx
================================ */
export async function fetchConferenceStats() {
  const { count, error } = await supabase
    .from("conference_registrations")
    .select("*", { count: "exact", head: true })

  if (error) {
    console.error("Conference count error:", error)
    return 0
  }

  return count || 0
}

/* ===============================
   Daily conference registrations
================================ */
export async function fetchConferenceDaily() {
  const { data, error } = await supabase
    .rpc("conference_daily_stats")

  if (error) {
    console.error("Conference daily stats error:", error)
    return []
  }

  return (data || []).map(row => ({
    label: row.day,
    value: Number(row.count)
  }))
}

/* ===============================
   Monthly conference registrations
================================ */
export async function fetchConferenceMonthly() {
  const { data, error } = await supabase
    .rpc("conference_monthly_stats")

  if (error) {
    console.error("Conference monthly stats error:", error)
    return []
  }

  return (data || []).map(row => ({
    label: row.month,
    value: Number(row.count)
  }))
}

/* ===============================
   Update registration status
   Used by ConferenceRegistrations.jsx
================================ */
export async function updateRegistrationStatus(id, status) {
  console.log("🔄 Updating registration:", { id, status })
  
  // First, check if the record exists
  const { data: existing, error: fetchError } = await supabase
    .from("conference_registrations")
    .select("id, status")
    .eq("id", id)
    .single()

  console.log("📋 Existing record:", { existing, fetchError })

  if (fetchError || !existing) {
    console.error("❌ Registration not found:", id, fetchError)
    return { 
      data: null, 
      error: { message: "Registration not found or status column doesn't exist. Please run the database migration first." }
    }
  }

  // Update the status
  const { data, error } = await supabase
    .from("conference_registrations")
    .update({ status })
    .eq("id", id)
    .select()

  console.log("✅ Update result:", { data, error })

  if (error) {
    console.error("❌ Update registration status error:", error)
    return { 
      data: null, 
      error: { 
        message: error.code === "42703" 
          ? "Status column doesn't exist. Please run the database migration first." 
          : error.message 
      }
    }
  }

  // Return the first (and only) updated record
  console.log("🎉 Status updated successfully:", data?.[0])
  return { data: data?.[0] || null, error: null }
}

/* ===============================
   Add new conference registration
   Used by ConferenceRegistrations.jsx
================================ */
export async function addConferenceRegistration(registrationData) {
  const { data, error } = await supabase
    .from("conference_registrations")
    .insert([{
      full_name: registrationData.fullName,
      email: registrationData.email,
      phone: registrationData.phone || null,
      organization: registrationData.organization || null,
      role: registrationData.role || null,
      sector: registrationData.sector || null,
      country: registrationData.country || null,
      status: "pending"
    }])
    .select()

  if (error) {
    console.error("Add conference registration error:", error)
    return { data: null, error }
  }

  return { data: data?.[0] || null, error: null }
}

/* ===============================
   Add new club membership application
   Used by ClubMemberships.jsx
================================ */
export async function addClubMembershipApplication(applicationData) {
  const { data, error } = await supabase
    .from("africamed_club_applications")
    .insert([{
      full_name: applicationData.fullName,
      email: applicationData.email,
      phone: applicationData.phone || null,
      country: applicationData.country || null,
      organization: applicationData.organization || null,
      role: applicationData.role || null,
      reason: applicationData.reason || null,
      admin_notes: null
    }])
    .select()

  if (error) {
    console.error("Add club membership error:", error)
    return { data: null, error }
  }

  return { data: data?.[0] || null, error: null }
}
